import { useState } from 'react';
import Image from 'next/image';
import { entries } from 'lodash';
import { useMedia } from 'react-use';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import cs from 'classnames';

import Section from 'react-scale/components/elements/section';
import TwitterIcon from 'react-scale/components/icons/Twitter';
import LinkedinIcon from 'react-scale/components/icons/Linkedin';
import CloseIcon from 'react-scale/components/icons/Close';
import Modal from 'react-scale/components/tailwind/Modal';
import usePreventScroll from 'react-scale/hooks/use-prevent-scroll';

const socialIcons = {
  twitter: <TwitterIcon fill="white" width="24" height="24" />,
  linkedin: <LinkedinIcon fill="white" width="24" height="24" />
};

const MAX_DESKTOP_CHARS = 315;
const MAX_MOBILE_CHARS = 120;

const Presenter = ({ onClick, presenter }) => {
  const isMobile = useMedia('(max-width: 1024px)');

  const MAX_CHARS = isMobile ? MAX_MOBILE_CHARS : MAX_DESKTOP_CHARS;

  return (
    <div
      onClick={presenter?.content?.length > MAX_CHARS ? onClick : null}
      className={cs(
        'flex flex-col justify-between h-full rounded-lg px-12 py-7 lg:p-6 bg-codGray max-w-[540px]',
        { 'cursor-pointer': presenter?.content?.length > MAX_CHARS }
      )}
    >
      <div>
        <div className="flex flex-col space-y-8 lg:flex-row lg:space-x-6 lg:space-y-0">
          <div>
            <Image
              className="rounded-full bg-transform-gradient-vertical"
              src={presenter?.picture}
              width="128"
              height="128"
            />
          </div>
          <div className="flex flex-col justify-center space-y-2 lg:space-y-4">
            <p className="text-lg lg:text-4xl">{presenter?.name}</p>
            <div>
              {presenter?.positions?.map(p => (
                <p className="text-sm text-silver" key={`speaker-position-${p.title}`}>
                  {p?.title}
                  {p?.company && <> at {p?.company}</>}
                </p>
              ))}
            </div>
          </div>
        </div>
        <p className="mt-2 text-sm leading-6 lg:mt-6 lg:text-base lg:leading-8">
          {presenter?.content?.length > MAX_CHARS ? (
            <>
              <ReactMarkdown
                components={{
                  p: ({ children }) => <p className="inline">{children}</p>
                }}
                rehypePlugins={[rehypeRaw]}
              >
                {presenter?.content.substr(0, MAX_CHARS)}
              </ReactMarkdown>
              ...
              <button
                className="block px-4 py-1 mx-auto my-8 text-sm font-medium uppercase transition-colors border rounded-full max-w-max lg:mx-0 text-scale-mauve border-scale-mauve hover:border-white hover:bg-white hover:text-black"
                type="button"
              >
                VIEW MORE
              </button>
            </>
          ) : (
            presenter.content
          )}
        </p>
      </div>
      <ul
        className={cs('flex justify-center lg:justify-start space-x-7', {
          'mt-6': presenter?.content?.length <= MAX_CHARS
        })}
      >
        {entries(presenter.social).map(
          ([key, value]) =>
            value && (
              <li key={key}>
                <a onClick={e => e.stopPropagation()} href={value} target="_blank" rel="noreferrer">
                  {socialIcons[key]}
                </a>
              </li>
            )
        )}
      </ul>
    </div>
  );
};

const PresenterModal = ({ hide, presenter, ...rest }) => {
  if (!presenter) return null;

  return (
    <Modal
      contentClassName="relative rounded-lg bg-codGray border-t-4 border-[#262626] max-w-[540px] lg:max-w-[760px] text-white !text-left p-8 lg:p-14 mx-5"
      hideCloseButton
      hide={hide}
      {...rest}
    >
      <button onClick={hide} className="absolute right-5 top-6" type="button">
        <CloseIcon fill="white" width="24" height="24" />
      </button>
      <div className="flex flex-col space-y-2 lg:flex-row lg:space-x-6 lg:space-y-0">
        <div>
          <div className="hidden lg:block">
            <Image
              className="rounded-full bg-transform-gradient-vertical"
              src={presenter.picture}
              width="128"
              height="128"
            />
          </div>
          <div className="block lg:hidden">
            <Image
              className="rounded-full bg-transform-gradient-vertical"
              src={presenter.picture}
              width="72"
              height="72"
            />
          </div>
        </div>
        <div className="flex flex-col justify-center space-y-2 lg:space-y-4">
          <p className="text-lg lg:text-4xl">{presenter.name}</p>
          <div>
            {presenter.positions?.map(p => (
              <p className="text-sm text-silver" key={`speaker-position-${p.title}`}>
                {p?.title}
                {p?.company && <> at {p?.company}</>}
              </p>
            ))}
          </div>
        </div>
      </div>
      <ReactMarkdown
        className="mt-2 text-sm leading-6 lg:mt-6 lg:text-base lg:leading-8"
        rehypePlugins={[rehypeRaw]}
      >
        {presenter.content}
      </ReactMarkdown>
      <ul className="flex justify-start mt-8 space-x-7">
        {entries(presenter.social).map(
          ([key, value]) =>
            value && (
              <li>
                <a href={value} target="_blank" rel="noreferrer">
                  {socialIcons[key]}
                </a>
              </li>
            )
        )}
      </ul>
    </Modal>
  );
};

const Presenters = ({ presenters }) => {
  const [activePresenterIdx, setActivePresenterIdx] = useState(null);

  usePreventScroll(activePresenterIdx !== null);

  return (
    <Section paddingTop={false} paddingBottom={false}>
      <PresenterModal
        hide={() => setActivePresenterIdx(null)}
        presenter={presenters[activePresenterIdx]}
        isShowing={activePresenterIdx !== null}
      />
      <h3 className="uppercase mx-auto max-w-max tracking-[4px] text-transform-gradient">
        Presented by
      </h3>
      <div className="flex flex-col flex-wrap items-center justify-between pt-16 -my-8 text-center lg:items-stretch lg:flex-row lg:justify-center lg:-m-8 lg:px-9 lg:text-left">
        {presenters?.map((p, idx) => (
          <div className="my-8 flex-grow-1 lg:mx-8" key={p.name}>
            <Presenter onClick={() => setActivePresenterIdx(idx)} presenter={p} />
          </div>
        ))}
      </div>
    </Section>
  );
};

export default Presenters;
